<template>
  <div class="main-sidebar-footer__wrapper">
    <div
      :class="{ 'main-sidebar-footer__category--region': !isClientPage }"
      class="main-sidebar-footer__category">
      <div class="main-sidebar-footer__category-title">
        Your region
      </div>

      <div class="main-sidebar-footer__menu">
        <header-select-widget
          :value="activeRegion"
          :items="RegionModel.LIST"
          :open-triggers="['click']"
          type="region"
          class="main-sidebar-footer__region-select"
          @on-select="setRegion" />
      </div>
    </div>

    <div class="main-sidebar-footer__categories-wrapper">
      <customer-category-links
        v-for="category in links"
        :key="category.title"
        :category="category"
        class="main-sidebar-footer__category">
        <template #title>
          <div class="main-sidebar-footer__category-title">
            {{ category.title }}
          </div>
        </template>

        <template #links="{ links }">
          <ul class="main-sidebar-footer__menu">
            <li
              v-for="item in links"
              :key="item.title"
              class="menu__item transition--color"
              @click="$emit('hideSidebar')">
              <component
                :is="item.isExternalLink ? 'a' : 'router-link'"
                v-if="!item.hideOnSidebar"
                :href="item._href"
                :rel="item.isNoFollow ? 'nofollow' : ''"
                class="menu__item-link"
                :to="item._href">
                {{ item.title }}
              </component>
            </li>
          </ul>
        </template>
      </customer-category-links>
    </div>

    <button
      class="main-sidebar-footer__button button button-main"
      @click="openIntercom">
      <img
        src="/images/icons/chat2.svg"
        width="15"
        height="15"
        class="button-icon">

      <span>
        LiveHelp — Chat with us
      </span>
    </button>

    <div class="main-sidebar-footer__contacts">
      <span class="contacts-item">
        <a
          class="contacts-item-link"
          href="tel:+18554011156">+1 855 401 11 56</a>
      </span>

      <lazy-hydrate never>
        <span class="contacts-item">
          <a
            class="contacts-item-link"
            href="mailto:support@skycoach.gg">
            support@skycoach.gg
          </a>
        </span>
      </lazy-hydrate>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import LazyHydrate from 'vue-lazy-hydration';
import googleTagAnalytics from 'Root/drivers/googleTagAnalytics';
import IntercomApiDriver from 'Root/drivers/IntercomApiDriver';
import { StaticLinksCompany, StaticLinksCustomerService, StaticLinksLegal } from 'Models/StaticLinksModel';
import CustomerCategoryLinks from 'Components/CustomerCategoryLinks.vue';
import HeaderSelectWidget from 'Components/MainHeader/HeaderSelectWidget.vue';
import RegionModel from 'Models/RegionModel';

export default {
  name: 'MainSidebarFooter',

  components: {
    LazyHydrate,
    CustomerCategoryLinks,
    HeaderSelectWidget,
  },

  data() {
    return {
      links: [
        StaticLinksCompany,
        StaticLinksCustomerService,
        StaticLinksLegal,
      ],
      RegionModel,
    };
  },

  computed: {
    ...mapState(['activeRegion']),

    isClientPage() {
      return this.$route.name.includes('client');
    },
  },

  methods: {
    ...mapMutations(['mutateActiveRegion']),
    openIntercom() {
      this.$emit('hideSidebar');
      googleTagAnalytics.emitItemOpenChatEvent();
      IntercomApiDriver.show();
    },

    setRegion(region) {
      this.mutateActiveRegion(region.id);
      this.$cookie.set(RegionModel.REGION_COOKIE, region.id, { expires: '5Y' });
      localStorage.setItem(RegionModel.REGION_COOKIE, region.id);
      googleTagAnalytics.emitSelectRegion(region.shortTitle);
    },
  },
};
</script>

<style lang="scss" scoped>
  .main-sidebar-footer__wrapper {
    padding: 24px 20px;
  }

  .main-sidebar-footer__category--region {
      display: block;
  }

  .main-sidebar-footer__category:not(:last-child) {
    border-bottom: 1px solid $color--basic-menu;
    padding-bottom: 16px;
    margin-bottom: 24px;
  }

  .main-sidebar-footer__category-title {
    text-transform: uppercase;
    color: $color--basic-dark-gray;
    font-weight: 500;
    letter-spacing: 0.1em;
    font-size: $text-13px;
    margin-bottom: 16px;
  }

  /deep/ .main-sidebar-footer__region-select {
    height: auto;

    .dropdown-widget__select {
      --bg-color: #{$color--basic-black};
      --bg-color--open: #{$color--basic-black};
      --options-bg-color: #{$color--basic-black};
      --height: auto;
    }

    .dropdown-widget__selected-wrapper {
      margin-left: 0;
    }

    .dropdown-widget__selected-title {
      display: block;
    }

    .dropdown-widget__options {
      background-color: $color--basic-black;
      border-radius: 0;
      padding-top: 5px;
      top: 16px;
      border-top: 1px solid $color--basic-menu;
    }

    .dropdown-widget__option {
      --icon-size: 24px;

      padding: 10px 13px;
    }

    .base-select__items {
      position: relative;
      width: auto;
      right: 0;
    }

    .ui-select__arrow-wrap {
      display: block;
    }
  }

  .main-sidebar-footer__button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 32px;
    border-radius: 5px;
    font-weight: 500;
    font-size: 13px;
    text-align: center;
    text-transform: inherit;
    margin: 22px 0 18px;

    .button-icon {
      margin-right: 8px;
    }
  }

  .menu__item {
    font-weight: 500;
    font-size: $text-15px;
    line-height: 1.2;
    margin-bottom: 8px;
    color: $color--basic-white;

    &:hover {
      color: $color--basic-green;
    }

    &:last-child {
      margin-bottom: 0;
    }

    .menu__item-link {
      color: currentColor;
    }
  }

  .main-sidebar-footer__contacts {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .contacts-item-link {
    font-weight: 500;
    font-size: 13px;
    color: $color--basic-white;
  }

  @media screen and (min-width: 600px) {
    .main-sidebar-footer__category--region {
      display: none;
    }
  }
</style>
